/**
 * The submit-state machine's vocabulary for the e-laskuehdotus surface (048).
 *
 * Split out of `EInvoiceProposalRequest.tsx` when it crossed the enforced
 * 400-line limit. Everything here is pure: a state union and the mapping from a
 * state to the copy key that states it. The component keeps the transitions,
 * the idempotency key and the focus handling.
 *
 * Owned by feature 048-member-einvoice-request.
 */

import type {
  EInvoiceEligibility,
  EInvoiceRequestRefusal,
} from '../../membership-billing/MembershipBillingPort.js';

export type EligibilityKind = EInvoiceEligibility['kind'];

/** Each refusal gets the copy it deserves — see the `refused` note below. */
const REFUSAL_KEYS = {
  'already-requested': 'profile.fee.eInvoice.errorAlreadyRequested',
  'unsupported-bank': 'profile.fee.eInvoice.errorUnsupportedBank',
  'retry-conflict': 'profile.fee.eInvoice.errorRetryConflict',
  // NOT the transient "try again shortly" text: the register refused outright
  // and the contract says do not retry unchanged, so this points at a human
  // instead of inviting a retry that cannot work (D-228(d), owner decision 2026-09-28).
  refused: 'profile.fee.eInvoice.errorRefused',
  unavailable: 'profile.fee.eInvoice.errorUnavailable',
} as const satisfies Record<EInvoiceRequestRefusal, string>;

export type Submission =
  | { readonly kind: 'idle' }
  | { readonly kind: 'sending' }
  | { readonly kind: 'sent' }
  | { readonly kind: 'unconfirmed' }
  /** The session expired: the only fix is signing in again, not retrying. */
  | { readonly kind: 'session-expired' }
  | { readonly kind: 'refused'; readonly reason: EInvoiceRequestRefusal };

/**
 * The copy keys this module can return.
 *
 * Deliberately a literal union rather than `string`: `useTranslate` is typed to
 * the known message keys, so a typo or a key deleted from the catalogue fails
 * typecheck here instead of rendering the raw key to a member.
 */
export type EInvoiceMessageKey =
  | 'profile.fee.eInvoice.submitting'
  | 'profile.fee.eInvoice.confirmation'
  | 'profile.fee.eInvoice.unconfirmed'
  | 'profile.fee.eInvoice.sessionExpired'
  | (typeof REFUSAL_KEYS)[EInvoiceRequestRefusal];

/**
 * The copy key that states this submission, or `null` for "say nothing".
 *
 * `superseded` is `isSupersededBy(submission, <re-read eligibility>)`: the
 * re-read has landed and the view it renders answers the outcome, so the
 * transient message says nothing next to it (contract "Result arms").
 */
export function submissionMessageKey(
  submission: Submission,
  superseded: boolean,
): EInvoiceMessageKey | null {
  if (superseded) return null;
  switch (submission.kind) {
    case 'sending':
      return 'profile.fee.eInvoice.submitting';
    case 'sent':
      return 'profile.fee.eInvoice.confirmation';
    case 'unconfirmed':
      return 'profile.fee.eInvoice.unconfirmed';
    case 'session-expired':
      return 'profile.fee.eInvoice.sessionExpired';
    case 'refused':
      return REFUSAL_KEYS[submission.reason];
    default:
      return null;
  }
}

/**
 * Whether the view the re-read renders answers this outcome (contract "Result
 * arms"), so its transient message must say nothing next to it.
 *
 * - `retry-conflict` / `unsupported-bank`: their copy ("try again in a moment",
 *   "choose a different bank") only makes sense while the member can still
 *   request. In any other state the request action is gone, so it would invite
 *   an action that is not offered — e.g. telling a member already on e-invoice
 *   to retry.
 * - `sent`, `already-requested`, `unavailable`: answered by a `pending` re-read.
 *   The pending block opens "E-laskuehdotus tilattu <date>" and carries the
 *   earliest re-request date: the confirmation repeated it (two stacked
 *   success messages), the already-requested line restates it, and an
 *   undetermined "didn't work, try again" is a failure claim it disproves.
 *
 * `unconfirmed`, `session-expired` and `refused` are never superseded — each
 * says something no re-read state does.
 */
export function isSupersededBy(submission: Submission, eligibility: EligibilityKind): boolean {
  if (eligibility === 'can-request') return false;
  if (submission.kind === 'sent') return eligibility === 'pending';
  if (submission.kind !== 'refused') return false;
  switch (submission.reason) {
    case 'retry-conflict':
    case 'unsupported-bank':
      return true;
    case 'already-requested':
    case 'unavailable':
      return eligibility === 'pending';
    default:
      return false;
  }
}

/**
 * Whether the request action is withheld after this submission.
 *
 * - `already-requested`: offering it again would invite the refusal the member
 *   just saw (copy §5).
 * - `refused` (`semantic-rejection`): the contract says do not retry unchanged;
 *   the toggle would wipe the "contact member services" message and reopen a
 *   form whose resubmit replays the same refusal (D-228(d)).
 * - `sent`: for the rest of the component's life — the intent is cleared, so a
 *   second click would send a NEW request with a fresh key. The re-read
 *   normally moves the view to `pending`; if it still says `can-request`, the
 *   action returns only on remount (deliberately).
 * - `session-expired`: the toggle resets the submission to idle, so leaving it
 *   live let one click wipe the "sign in again" message and invite a resubmit
 *   against the same dead session. Signing in again remounts the component.
 */
export function hidesAction(submission: Submission): boolean {
  if (submission.kind === 'sent' || submission.kind === 'session-expired') return true;
  return (
    submission.kind === 'refused' &&
    (submission.reason === 'already-requested' || submission.reason === 'refused')
  );
}
